// Librairies
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal, Signal, WritableSignal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

// Variables
// Material Icons names (ligatures of the font already loaded): no extra download, any valid name renders.
export const PICKER_ICONS: readonly string[] = [
  // Safety and risks
  'health_and_safety', 'crisis_alert', 'warning', 'report', 'report_problem', 'dangerous', 'error', 'priority_high', 'shield', 'security',
  'verified_user', 'gpp_maybe', 'medical_services', 'local_hospital', 'masks', 'sports_motorsports', 'emergency', 'fire_extinguisher',
  // Site and production
  'engineering', 'construction', 'handyman', 'build', 'hardware', 'precision_manufacturing', 'factory', 'apartment', 'home_work',
  'foundation', 'roofing', 'architecture', 'carpenter', 'plumbing', 'electrical_services', 'local_shipping', 'agriculture', 'inventory',
  'directions_car', 'directions_walk', 'groups', 'person', 'badge', 'support_agent',
  // Environment
  'eco', 'park', 'recycling', 'water_drop', 'air', 'co2', 'bolt', 'thermostat', 'wb_sunny', 'cloud', 'thunderstorm', 'volume_up', 'noise_aware',
  // Data and charts
  'insights', 'trending_up', 'trending_down', 'trending_flat', 'show_chart', 'bar_chart', 'stacked_bar_chart', 'pie_chart', 'donut_small',
  'leaderboard', 'analytics', 'query_stats', 'table_chart', 'grid_on', 'timeline', 'speed', 'percent', 'functions', 'calculate',
  // Time
  'schedule', 'timer', 'hourglass_empty', 'event', 'calendar_month', 'date_range', 'history', 'update',
  // Places and devices
  'location_on', 'place', 'map', 'public', 'explore', 'sensors', 'videocam', 'photo_camera', 'image', 'visibility', 'radar', 'wifi',
  // Tasks and alerts
  'task_alt', 'check_circle', 'pending_actions', 'assignment', 'checklist', 'fact_check', 'notifications', 'campaign', 'flag', 'star',
  'bookmark', 'label', 'category', 'lightbulb', 'tips_and_updates', 'info', 'help',
];

// Classes
export function filterIcons(icons: readonly string[], search: string): string[] {
  const words = search.trim().toLowerCase().replace(/\s+/g, '_');
  return words ? icons.filter(icon => icon.includes(words)) : [...icons];
}

// A field showing the chosen Material icon; its panel searches a list, or takes any Material icon name.
@Component({
  selector: 'lib-icon-picker',
  standalone: true,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TranslateModule
  ],
  templateUrl: './icon-picker.component.html',
  styleUrl: './icon-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class IconPickerComponent {
  // Variables
  @Input() value: string | null = null;
  @Input() label: string = '';                       // i18n key
  @Input() icons: readonly string[] = PICKER_ICONS;
  @Output() valueChange: EventEmitter<string | null> = new EventEmitter();

  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  // Signals
  readonly open: WritableSignal<boolean> = signal(false);
  readonly search: WritableSignal<string> = signal('');
  readonly visibleIcons: Signal<string[]> = computed(() => filterIcons(this.icons, this.search()));

  // A typed name that is not in the list: offered as is (any Material icon renders).
  readonly custom: Signal<string | null> = computed(() => {
    const name = this.search().trim().toLowerCase().replace(/\s+/g, '_');
    return /^[a-z0-9_]+$/.test(name) && !this.icons.includes(name) ? name : null;
  });

  toggle(): void {
    this.open.set(!this.open());
    this.search.set('');
  }

  choose(icon: string | null): void {
    this.value = icon;
    this.valueChange.emit(icon);
    this.open.set(false);
  }
}
